import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Icon } from '@iconify/react'
import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Chip from '@mui/material/Chip'
import InputAdornment from '@mui/material/InputAdornment'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import {
  formatGpsLat,
  formatGpsLon,
  gpsPointKey,
  gpsPoints,
  gpsReference,
  sanitiseIdentifier,
  userWaypointsToCreate,
  type GpsPoint,
} from '@jupi/shared'
import { IconButton, RemoteContent, Title, useSnackbar } from '@jupi/ui'
import { useWaypoints } from '../../api/plan'
import { usePlanDraft } from './usePlanDraft'

// The flight plan as a list to key into a panel GPS (Garmin GNS 430): which
// points the unit already knows, and the ones to make as user waypoints
// first, with a five-character identifier suggested for each.

// The pilot's own identifiers, by point: a per-device convenience.
const NAMES_KEY = 'jupi.plan.gps.names'

function loadNames(): Record<string, string> {
  try {
    return JSON.parse(localStorage.getItem(NAMES_KEY) ?? '{}') as Record<string, string>
  } catch {
    return {}
  }
}

const pad3 = (n: number) => String(n).padStart(3, '0')

const place = (p: GpsPoint) => (p.lat === null || p.lon === null ? null : `${formatGpsLat(p.lat)}  ${formatGpsLon(p.lon)}`)

export default function GpsPage() {
  const { t } = useTranslation()
  const { showSnackbar } = useSnackbar()
  const editor = usePlanDraft()
  const { data: waypoints = [] } = useWaypoints()
  const [names, setNames] = useState(loadNames)
  const rename = (key: string, text: string) => {
    const next = { ...names }
    if (text) next[key] = text
    else delete next[key]
    setNames(next)
    try {
      localStorage.setItem(NAMES_KEY, JSON.stringify(next))
    } catch {
      // Private mode: the name just is not remembered.
    }
  }
  const points = useMemo(
    () =>
      gpsPoints(
        editor.draft?.legs ?? [],
        new Map(waypoints.map((w) => [w.code, w.waypointType])),
        new Map(Object.entries(names)),
      ),
    [editor.draft, waypoints, names],
  )
  const toCreate = userWaypointsToCreate(points)
  // The unit knows aerodromes, so a point can be defined from the nearest.
  const known = useMemo(
    () => waypoints.filter((w) => w.waypointType === 'ad').map((w) => ({ code: w.code, lat: w.lat, lon: w.lon, variation: w.variation })),
    [waypoints],
  )

  const refText = (p: GpsPoint) => {
    const ref = gpsReference(p, known)
    return ref ? `  (${ref.code} ${pad3(ref.radial)}° ${ref.distanceNm.toFixed(1)} NM)` : ''
  }

  const copy = async () => {
    const lines = [
      ...(toCreate.length ? [t('plan.gps.create'), ...toCreate.map((p) => `${p.identifier}  ${place(p) ?? ''}${refText(p)}`), ''] : []),
      t('plan.gps.route'),
      points.map((p) => p.identifier).join(' > '),
    ]
    try {
      await navigator.clipboard.writeText(lines.join('\n'))
      showSnackbar(t('plan.gps.copied'))
    } catch {
      showSnackbar(t('plan.gps.copyFailed'), { severity: 'error' })
    }
  }

  return (
    <Box sx={{ maxWidth: 640 }}>
      <Title label={t('nav.planGps')} />
      <RemoteContent isLoading={editor.isLoading} error={editor.error}>
        {points.length === 0 ? (
          <Alert severity="info">{t('plan.editor.empty')}</Alert>
        ) : (
          <Stack spacing={2}>
            {toCreate.length > 0 ? (
              <Alert severity="warning">{t('plan.gps.needCreate', { count: toCreate.length })}</Alert>
            ) : (
              <Alert severity="success">{t('plan.gps.allKnown')}</Alert>
            )}

            {toCreate.length > 0 && (
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t('plan.gps.create')}
                  </Typography>
                  <Stack spacing={1.5} divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />}>
                    {toCreate.map((p) => (
                      <UserPoint
                        key={gpsPointKey({ code: p.code, lat: p.lat, lon: p.lon })}
                        point={p}
                        typed={names[gpsPointKey({ code: p.code, lat: p.lat, lon: p.lon })] ?? ''}
                        onRename={(text) => rename(gpsPointKey({ code: p.code, lat: p.lat, lon: p.lon }), text)}
                        reference={gpsReference(p, known)}
                      />
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('plan.gps.route')}
                </Typography>
                <Stack spacing={1}>
                  {points.map((p) => (
                    <Stack key={p.index} direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ width: 20 }}>
                        {p.index + 1}
                      </Typography>
                      <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, minWidth: 64 }}>{p.identifier}</Typography>
                      <Chip
                        size="small"
                        color={p.kind === 'user' ? 'warning' : 'default'}
                        variant={p.kind === 'user' ? 'filled' : 'outlined'}
                        label={t(p.kind === 'user' ? 'plan.gps.kindUser' : 'plan.gps.kindDatabase')}
                      />
                      <Typography variant="body2" color="text.secondary" noWrap sx={{ flex: 1, minWidth: 0 }}>
                        {p.name}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </CardContent>
            </Card>

            <Box>
              <Button startIcon={<Icon icon="mdi:content-copy" />} onClick={() => void copy()}>
                {t('plan.gps.copy')}
              </Button>
            </Box>

            {[
              { title: 'howTitle', steps: [1, 2, 3, 4, 5].map((n) => `how${n}`) },
              { title: 'fplTitle', steps: [1, 2, 3, 4, 5, 6].map((n) => `fpl${n}`), sequence: true },
              { title: 'storedTitle', steps: [1, 2, 3, 4, 5].map((n) => `stored${n}`), intro: 'storedIntro' },
            ].map(({ title, steps, sequence, intro }) => (
              <Accordion key={title} disableGutters>
                <AccordionSummary expandIcon={<Icon icon="mdi:chevron-down" />}>{t(`plan.gps.${title}`)}</AccordionSummary>
                <AccordionDetails>
                  {intro && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {t(`plan.gps.${intro}`)}
                    </Typography>
                  )}
                  <ol style={{ margin: 0, paddingLeft: 20 }}>
                    {steps.map((step) => (
                      <li key={step}>{t(`plan.gps.${step}`)}</li>
                    ))}
                  </ol>
                  {sequence && (
                    <>
                      <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, mt: 1.5 }}>
                        {points.map((p) => p.identifier).join('  ')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('plan.gps.fplNote')}
                      </Typography>
                    </>
                  )}
                </AccordionDetails>
              </Accordion>
            ))}
          </Stack>
        )}
      </RemoteContent>
    </Box>
  )
}

function UserPoint({
  point,
  typed,
  onRename,
  reference,
}: {
  point: GpsPoint
  /** What the pilot typed, which may not be what they got. */
  typed: string
  onRename: (text: string) => void
  reference: ReturnType<typeof gpsReference>
}) {
  const { t } = useTranslation()
  const clashes = Boolean(typed) && sanitiseIdentifier(typed) !== point.identifier
  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
        <TextField
          size="small"
          label={t('plan.gps.identifier')}
          value={typed || point.identifier}
          onChange={(e) => onRename(sanitiseIdentifier(e.target.value))}
          error={clashes}
          helperText={clashes ? t('plan.gps.identifierTaken') : (point.name ?? (point.code === point.identifier ? '' : point.code))}
          slotProps={{
            htmlInput: { maxLength: 5, autoCapitalize: 'characters', autoComplete: 'off', style: { fontFamily: 'monospace', fontWeight: 700, fontSize: '1.2rem' } },
            input: {
              endAdornment: typed ? (
                <InputAdornment position="end">
                  <IconButton size="small" aria-label={t('plan.gps.useSuggested')} onClick={() => onRename('')}>
                    <Icon icon="mdi:restore" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            },
          }}
          sx={{ width: 170 }}
        />
      </Stack>
      <Typography sx={{ fontFamily: 'monospace', mt: 0.5 }}>{place(point) ?? t('plan.editor.noPosition')}</Typography>
      {point.lat !== null && point.lon !== null && (
        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', display: 'block' }}>
          {point.lat.toFixed(5)}, {point.lon.toFixed(5)}
        </Typography>
      )}
      {reference && (
        <Typography sx={{ fontFamily: 'monospace' }}>
          {t('plan.gps.refLine', { code: reference.code, radial: pad3(reference.radial), distance: reference.distanceNm.toFixed(1) })}
        </Typography>
      )}
    </Box>
  )
}
